import { Badge } from "@/components/ui/badge";
import { MapLocation } from '@/types/location';
import { useLanguage } from "@/contexts/LanguageContext";
import { MapPin, Building2, Wrench, WifiOff, Calendar } from 'lucide-react';

interface PopupHeaderProps {
  location: MapLocation;
  isSelected: boolean;
  selectedScreenCount: number;
  hasPartialSelection: boolean;
}

const formatMaintenanceDate = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const parseMaintenanceNotes = (notes: string | null | undefined) => {
  if (!notes) return null;
  try {
    const parsed = typeof notes === 'string' ? JSON.parse(notes) : notes;
    // DB format: { scheduled_start, scheduled_end, notes, history }
    if (parsed?.scheduled_start || parsed?.scheduled_end) {
      return {
        reason: parsed.notes || 'Scheduled Maintenance',
        startDate: parsed.scheduled_start || null,
        endDate: parsed.scheduled_end || null,
      };
    }
    // Legacy format: { current: { startDate, endDate, notes } }
    if (parsed?.current) {
      return {
        reason: parsed.current.notes || 'Scheduled Maintenance',
        startDate: parsed.current.startDate || null,
        endDate: parsed.current.endDate || null,
      };
    }
    return null;
  } catch {
    return null;
  }
};

export const PopupHeader = ({
  location,
  isSelected,
  selectedScreenCount,
  hasPartialSelection
}: PopupHeaderProps) => {
  const { t } = useLanguage();
  const isAttentionLocation = location.mediaOwner === 'Attention';

  const maintenanceInfo = location.maintenanceStatus === 'maintenance'
    ? parseMaintenanceNotes(location.maintenanceNotes)
    : null;

  const maintenanceBadge = (() => {
    const s = location.maintenanceStatus;
    if (!s || s === 'operational') return null;
    if (s === 'maintenance') {
      return (
        <div className="space-y-2 w-full">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs font-medium border border-amber-200 dark:border-amber-800/40">
            <Wrench className="h-3 w-3" />
            Under Maintenance
          </span>
          {maintenanceInfo && (
            <div className="bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/40 dark:border-amber-800/30 rounded-xl p-2.5 space-y-1.5">
              <div className="flex items-start gap-2">
                <Wrench className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-amber-800 dark:text-amber-300 leading-snug">{maintenanceInfo.reason}</span>
              </div>
              {maintenanceInfo.startDate && maintenanceInfo.endDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    {formatMaintenanceDate(maintenanceInfo.startDate)} → {formatMaintenanceDate(maintenanceInfo.endDate)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    if (s === 'offline') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-400 text-xs font-medium border border-red-200 dark:border-red-800/40">
          <WifiOff className="h-3 w-3" />
          Offline
        </span>
      );
    }
    return null;
  })();

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-xl text-foreground leading-tight">{location.name}</h3>
        {isAttentionLocation && isSelected && (
          <Badge
            variant={hasPartialSelection ? "secondary" : "default"}
            className="flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold"
          >
            {selectedScreenCount}/{location.devices} {t("screens")}
          </Badge>
        )}
      </div>

      <div className="flex items-center flex-wrap gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/70 text-muted-foreground text-xs font-medium border border-border/40">
          <MapPin className="h-3 w-3" />
          {location.region}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/70 text-muted-foreground text-xs font-medium border border-border/40">
          <Building2 className="h-3 w-3" />
          {location.mediaOwner}
        </span>
        {maintenanceBadge}
      </div>
    </div>
  );
};
